import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Select,
  Input,
  Button,
  Space,
  Typography,
  Spin,
  Row,
  Col,
  Tooltip,
  message,
  Divider,
  Tag,
  Switch,
  Tabs,
  Skeleton,
  Alert,
} from 'antd';
import {
  ThunderboltOutlined,
  CopyOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  ApiOutlined,
  CheckOutlined,
  ExpandOutlined,
  CompressOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useTheme } from '../App';
import ReactAce from 'react-ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-cobalt';
import 'ace-builds/src-noconflict/theme-github';

const { Option } = Select;
const { Text, Title, Paragraph } = Typography;

function EdgeFunctionSandbox({
  // Environment props
  selectedEnv,
  onSelectEnv,
  orgApiKeys,
  // Project props
  projects,
  selectedProject,
  onSelectProject,
  isLoadingProjects,
  // Edge function props
  edgeFunctions,
  selectedEdgeFunction,
  onSelectFunction,
  edgeFunctionJwt,
  playerToken,
  onPlayerTokenChange,
  onRunFunction,
  response,
  isLoading,
  isFetchingFunctions,
  onRefreshFunctions,
}) {
  const { theme, currentTheme } = useTheme();

  // Environment options
  const environments = [
    { value: 'development', label: 'Development' },
    { value: 'staging', label: 'Staging' },
    { value: 'production', label: 'Production' },
  ];

  // Get ORG API key based on selected environment
  // Falls back to development API key if no key available for selected environment
  const getApiKey = () => {
    if (!orgApiKeys) return '';

    let key = '';

    if (selectedEnv === 'development') {
      key = orgApiKeys.devAPIKey || '';
    } else if (selectedEnv === 'staging') {
      key = orgApiKeys.stagingAPIKey || '';
    } else if (selectedEnv === 'production') {
      key = orgApiKeys.productionAPIKey || '';
    }

    // Fallback to development if no key found for selected environment
    if (!key) {
      key = orgApiKeys.devAPIKey || '';
    }

    return key;
  };

  const apiKey = getApiKey();

  // Local state
  const [showJwt, setShowJwt] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [includePlayerToken, setIncludePlayerToken] = useState(false);
  const [payload, setPayload] = useState('{\n  \n}');
  const [isEditorExpanded, setIsEditorExpanded] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [customHeaders, setCustomHeaders] = useState([]);
  const [showHeadersPreview, setShowHeadersPreview] = useState(false);
  const [responseTab, setResponseTab] = useState('body');

  // Styles
  const cardStyle = {
    backgroundColor: theme.surface,
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
    marginBottom: '16px',
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
  };

  const inputStyle = {
    backgroundColor: theme.surfaceLight,
    borderColor: theme.border,
    color: theme.text,
  };

  // Helper functions
  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      message.success(`${fieldName} copied to clipboard`);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const isValidJson = (str) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  const formatPayload = () => {
    try {
      const parsed = JSON.parse(payload);
      setPayload(JSON.stringify(parsed, null, 2));
      message.success('JSON formatted');
    } catch (e) {
      message.error('Cannot format invalid JSON');
    }
  };

  // Custom headers management
  const addCustomHeader = () => {
    setCustomHeaders([...customHeaders, { key: '', value: '' }]);
  };

  const removeCustomHeader = (index) => {
    const newHeaders = customHeaders.filter((_, i) => i !== index);
    setCustomHeaders(newHeaders);
  };

  const updateCustomHeader = (index, field, value) => {
    const newHeaders = [...customHeaders];
    newHeaders[index][field] = value;
    setCustomHeaders(newHeaders);
  };

  // Build headers object for display and request
  const buildHeadersObject = () => {
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${edgeFunctionJwt || '<edge-jwt>'}`,
      'x-api-key': apiKey || '<api-key>',
    };

    if (includePlayerToken && playerToken) {
      headers['x-player-token'] = `Bearer ${playerToken}`;
    }

    // Add custom headers
    customHeaders.forEach(header => {
      if (header.key && header.value) {
        headers[header.key] = header.value;
      }
    });

    return headers;
  };

  const handleRunFunction = () => {
    if (!selectedEdgeFunction) {
      message.warning('Please select an edge function');
      return;
    }
    if (!isValidJson(payload)) {
      message.error('Please fix the JSON payload before running');
      return;
    }

    try {
      const parsedPayload = JSON.parse(payload);

      // Build custom headers object
      const customHeadersObj = {};
      customHeaders.forEach(header => {
        if (header.key && header.value) {
          customHeadersObj[header.key] = header.value;
        }
      });

      onRunFunction({
        denoUrl: selectedEdgeFunction.denoUrl,
        edgeJwt: edgeFunctionJwt,
        apiKey: apiKey,
        playerToken: includePlayerToken ? playerToken : null,
        payload: parsedPayload,
        customHeaders: customHeadersObj,
      });
    } catch (e) {
      message.error('Failed to parse payload');
    }
  };

  const getEndpointUrl = () => {
    if (!selectedEdgeFunction) return 'https://{deno-url}';
    return selectedEdgeFunction.denoUrl || '';
  };

  const generateCurlCommand = () => {
    if (!selectedEdgeFunction) return '';

    const url = getEndpointUrl();
    let parsedPayload = {};
    try {
      parsedPayload = JSON.parse(payload);
    } catch (e) {
      parsedPayload = {};
    }

    const body = JSON.stringify(parsedPayload, null, 2);

    let curl = `curl -X POST '${url}' \\\n`;
    curl += `  -H 'Content-Type: application/json' \\\n`;
    curl += `  -H 'Authorization: Bearer ${edgeFunctionJwt || '<edge-jwt>'}' \\\n`;
    curl += `  -H 'x-api-key: ${apiKey || '<api-key>'}'`;

    if (includePlayerToken && playerToken) {
      curl += ` \\\n  -H 'x-player-token: Bearer ${playerToken}'`;
    }

    // Add custom headers to curl
    customHeaders.forEach(header => {
      if (header.key && header.value) {
        curl += ` \\\n  -H '${header.key}: ${header.value}'`;
      }
    });

    curl += ` \\\n  -d '${body}'`;

    return curl;
  };

  // Keyboard shortcut: Ctrl/Cmd + Enter to run function
  const handleKeyboardRun = useCallback(() => {
    if (selectedEdgeFunction && isValidJson(payload) && edgeFunctionJwt && !isLoading) {
      handleRunFunction();
    }
  }, [selectedEdgeFunction, payload, edgeFunctionJwt, isLoading]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleKeyboardRun();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyboardRun]);

  // Check if we have projects loaded
  const hasProjects = projects && projects.length > 0;
  const hasFunctions = edgeFunctions && edgeFunctions.length > 0;

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          backgroundColor: theme.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <ThunderboltOutlined style={{ fontSize: 24, color: '#fff' }} />
        </div>
        <div>
          <Title level={3} style={{ margin: 0, color: theme.text }}>
            Edge Functions Sandbox
          </Title>
          <Text type="secondary">
            Test your deployed edge functions with custom payloads
          </Text>
        </div>
      </div>

      <Row gutter={[24, 24]}>
        {/* Left Column - Configuration */}
        <Col xs={24} lg={12}>
          {/* Project & Function Selector Card */}
          <Card
            title={
              <Space>
                <ApiOutlined style={{ color: theme.primary }} />
                <span style={{ color: theme.text }}>Select Function</span>
              </Space>
            }
            style={cardStyle}
            extra={
              <Button
                type="text"
                icon={<ReloadOutlined spin={isFetchingFunctions} />}
                onClick={onRefreshFunctions}
                disabled={!selectedProject}
              >
                Refresh
              </Button>
            }
          >
            {/* Environment Selector */}
            <div style={{ marginBottom: '16px' }}>
              <Text strong style={{ color: theme.text, display: 'block', marginBottom: '8px' }}>
                Environment
              </Text>
              <Select
                placeholder="Select an environment"
                style={{ width: '100%' }}
                value={selectedEnv}
                onChange={onSelectEnv}
              >
                {environments.map(env => (
                  <Option key={env.value} value={env.value}>
                    {env.label}
                  </Option>
                ))}
              </Select>
            </div>

            {/* Project Selector */}
            <div style={{ marginBottom: '16px' }}>
              <Text strong style={{ color: theme.text, display: 'block', marginBottom: '8px' }}>
                Project / App
              </Text>
              {!selectedEnv ? (
                <Alert
                  message="Select an environment first"
                  type="info"
                  showIcon
                />
              ) : isLoadingProjects ? (
                <Skeleton.Input active style={{ width: '100%' }} />
              ) : !hasProjects ? (
                <Alert
                  message="No Projects Found"
                  description="No projects available for this environment."
                  type="warning"
                  showIcon
                />
              ) : (
                <Select
                  placeholder="Select a project"
                  style={{ width: '100%' }}
                  value={selectedProject?.id}
                  onChange={(value) => {
                    const project = projects.find(p => p.id === value);
                    if (project) onSelectProject(project);
                  }}
                >
                  {projects.map(project => (
                    <Option key={project.id} value={project.id}>
                      {project.name}
                    </Option>
                  ))}
                </Select>
              )}
            </div>

            {/* Function Selector */}
            <div>
              <Text strong style={{ color: theme.text, display: 'block', marginBottom: '8px' }}>
                Edge Function
              </Text>
              {isFetchingFunctions ? (
                <Skeleton.Input active style={{ width: '100%' }} />
              ) : !selectedProject ? (
                <Alert
                  message="Select a project first"
                  type="info"
                  showIcon
                  style={{ marginTop: '8px' }}
                />
              ) : edgeFunctions.length === 0 ? (
                <Alert
                  message="No Edge Functions Found"
                  description={
                    <span>
                      No deployed edge functions in this project.{' '}
                      <a
                        href="https://console.specterapp.xyz"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: theme.primary }}
                      >
                        Go to Console to create one
                      </a>
                    </span>
                  }
                  type="warning"
                  showIcon
                  style={{ marginTop: '8px' }}
                />
              ) : (
                <Select
                  placeholder="Select an edge function"
                  style={{ width: '100%' }}
                  value={selectedEdgeFunction?.id}
                  onChange={(value) => {
                    const func = edgeFunctions.find(f => f.id === value);
                    if (func) onSelectFunction(func);
                  }}
                >
                  {edgeFunctions.map(func => (
                    <Option key={func.id} value={func.id}>
                      <Space>
                        <ThunderboltOutlined style={{ color: theme.accent }} />
                        <span>{func.name}</span>
                        <Tag color="green" style={{ marginLeft: '8px' }}>deployed</Tag>
                      </Space>
                    </Option>
                  ))}
                </Select>
              )}
            </div>

            {/* Endpoint Preview */}
            {selectedEdgeFunction && (
              <div style={{ marginTop: '16px' }}>
                <Text strong style={{ color: theme.text, display: 'block', marginBottom: '8px' }}>
                  Endpoint
                </Text>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px',
                  backgroundColor: theme.surfaceLight,
                  borderRadius: '8px',
                  border: `1px solid ${theme.border}`,
                }}>
                  <Tag color="blue" style={{ margin: 0 }}>POST</Tag>
                  <Text
                    style={{
                      color: theme.primary,
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {getEndpointUrl()}
                  </Text>
                  <Tooltip title="Copy URL">
                    <Button
                      type="text"
                      size="small"
                      icon={copiedField === 'url' ? <CheckOutlined /> : <CopyOutlined />}
                      onClick={() => copyToClipboard(getEndpointUrl(), 'url')}
                    />
                  </Tooltip>
                </div>
              </div>
            )}
          </Card>

          {/* Authentication Card */}
          <Card
            title={
              <Space>
                <span style={{ color: theme.text }}>Authentication</span>
              </Space>
            }
            style={cardStyle}
          >
            {/* Edge JWT */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <Text strong style={{ color: theme.text }}>Edge JWT</Text>
                <Space>
                  <Tooltip title={showJwt ? "Hide" : "Show"}>
                    <Button
                      type="text"
                      size="small"
                      icon={showJwt ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      onClick={() => setShowJwt(!showJwt)}
                    />
                  </Tooltip>
                  <Tooltip title="Copy">
                    <Button
                      type="text"
                      size="small"
                      icon={copiedField === 'jwt' ? <CheckOutlined /> : <CopyOutlined />}
                      onClick={() => copyToClipboard(edgeFunctionJwt, 'jwt')}
                      disabled={!edgeFunctionJwt}
                    />
                  </Tooltip>
                </Space>
              </div>
              <Input.TextArea
                value={showJwt ? edgeFunctionJwt : (edgeFunctionJwt ? '••••••••••••••••••••••••••••••••' : '')}
                readOnly
                rows={2}
                style={{
                  ...inputStyle,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                }}
                placeholder="JWT will be loaded when you select a project"
              />
              <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                Auto-loaded from project. Used in Authorization header.
              </Text>
            </div>

            {/* API Key */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <Text strong style={{ color: theme.text }}>API Key</Text>
                <Space>
                  <Tooltip title={showApiKey ? "Hide" : "Show"}>
                    <Button
                      type="text"
                      size="small"
                      icon={showApiKey ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      onClick={() => setShowApiKey(!showApiKey)}
                    />
                  </Tooltip>
                  <Tooltip title="Copy">
                    <Button
                      type="text"
                      size="small"
                      icon={copiedField === 'apiKey' ? <CheckOutlined /> : <CopyOutlined />}
                      onClick={() => copyToClipboard(apiKey, 'apiKey')}
                      disabled={!apiKey}
                    />
                  </Tooltip>
                </Space>
              </div>
              <Input
                value={showApiKey ? apiKey : (apiKey ? '••••••••••••••••••••••••' : '')}
                readOnly
                style={{ ...inputStyle, fontFamily: 'monospace' }}
                placeholder="API Key from your credentials"
              />
              <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                Your project API key. Sent as x-api-key header.
              </Text>
            </div>

            {/* Player Token (Optional) */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <Space>
                  <Text strong style={{ color: theme.text }}>Player Token</Text>
                  <Tag>Optional</Tag>
                </Space>
                <Switch
                  checked={includePlayerToken}
                  onChange={setIncludePlayerToken}
                  size="small"
                />
              </div>
              {includePlayerToken && (
                <>
                  <Input
                    value={playerToken}
                    onChange={(e) => onPlayerTokenChange(e.target.value)}
                    style={{ ...inputStyle, fontFamily: 'monospace' }}
                    placeholder="Enter player token for authenticated requests"
                  />
                  <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    Include if your function needs player context. Sent as x-player-token header.
                  </Text>
                </>
              )}
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* Custom Headers */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <Space>
                  <Text strong style={{ color: theme.text }}>Custom Headers</Text>
                  <Tag>Optional</Tag>
                </Space>
                <Button
                  type="dashed"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={addCustomHeader}
                >
                  Add Header
                </Button>
              </div>

              {customHeaders.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {customHeaders.map((header, index) => (
                    <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <Input
                        placeholder="Header name"
                        value={header.key}
                        onChange={(e) => updateCustomHeader(index, 'key', e.target.value)}
                        style={{ ...inputStyle, flex: 1 }}
                      />
                      <Input
                        placeholder="Header value"
                        value={header.value}
                        onChange={(e) => updateCustomHeader(index, 'value', e.target.value)}
                        style={{ ...inputStyle, flex: 2 }}
                      />
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => removeCustomHeader(index)}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  No custom headers. Click "Add Header" to include additional headers.
                </Text>
              )}
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* Headers Preview */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <Space>
                  <InfoCircleOutlined style={{ color: theme.primary }} />
                  <Text strong style={{ color: theme.text }}>Headers Preview</Text>
                </Space>
                <Button
                  type="text"
                  size="small"
                  onClick={() => setShowHeadersPreview(!showHeadersPreview)}
                >
                  {showHeadersPreview ? 'Hide' : 'Show'}
                </Button>
              </div>

              {showHeadersPreview && (
                <div style={{
                  backgroundColor: currentTheme === 'dark' ? '#1a1a2e' : '#f6f8fa',
                  borderRadius: '8px',
                  padding: '12px',
                  border: `1px solid ${theme.border}`,
                }}>
                  {Object.entries(buildHeadersObject()).map(([key, value]) => (
                    <div key={key} style={{ marginBottom: '4px', fontFamily: 'monospace', fontSize: '12px' }}>
                      <Text style={{ color: theme.primary }}>{key}:</Text>{' '}
                      <Text style={{ color: theme.textSecondary }}>
                        {key === 'Authorization' ? `Bearer ${'•'.repeat(20)}...` :
                         key === 'x-api-key' ? `${'•'.repeat(16)}...` :
                         value}
                      </Text>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </Col>

        {/* Right Column - Payload & Response */}
        <Col xs={24} lg={12}>
          {/* Payload Editor Card */}
          <Card
            title={
              <Space>
                <span style={{ color: theme.text }}>Request Payload</span>
                {!isValidJson(payload) && (
                  <Tag color="error">Invalid JSON</Tag>
                )}
              </Space>
            }
            style={cardStyle}
            extra={
              <Space>
                <Button size="small" onClick={formatPayload}>
                  Format
                </Button>
                <Button
                  size="small"
                  icon={copiedField === 'payload' ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={() => copyToClipboard(payload, 'payload')}
                >
                  Copy
                </Button>
                <Button
                  size="small"
                  icon={isEditorExpanded ? <CompressOutlined /> : <ExpandOutlined />}
                  onClick={() => setIsEditorExpanded(!isEditorExpanded)}
                />
              </Space>
            }
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
              Enter the data to send. This will be wrapped in <code style={{ color: theme.primary }}>{'{ "data": ... }'}</code>
            </Text>

            <div style={{
              borderRadius: '8px',
              overflow: 'hidden',
              border: `1px solid ${theme.border}`,
            }}>
              <ReactAce
                mode="json"
                theme={currentTheme === 'dark' ? 'cobalt' : 'github'}
                value={payload}
                onChange={setPayload}
                height={isEditorExpanded ? '400px' : '200px'}
                width="100%"
                setOptions={{
                  useWorker: false,
                  wrap: true,
                  printMargin: false,
                  showGutter: true,
                  fontSize: 14,
                  tabSize: 2,
                }}
                style={{
                  fontFamily: "'Fira Code', 'Source Code Pro', monospace",
                }}
                editorProps={{ $blockScrolling: true }}
              />
            </div>

            {/* Run Button */}
            <Tooltip
              title={
                !selectedEdgeFunction
                  ? 'Select an edge function first'
                  : !isValidJson(payload)
                  ? 'Fix the JSON payload first'
                  : !edgeFunctionJwt
                  ? 'JWT not loaded'
                  : 'Press Ctrl+Enter (Cmd+Enter on Mac) to run'
              }
            >
              <Button
                type="primary"
                size="large"
                icon={<PlayCircleOutlined />}
                onClick={handleRunFunction}
                loading={isLoading}
                disabled={!selectedEdgeFunction || !isValidJson(payload) || !edgeFunctionJwt}
                style={{
                  width: '100%',
                  marginTop: '16px',
                  height: '48px',
                  fontSize: '16px',
                  fontWeight: 600,
                  backgroundColor: !selectedEdgeFunction || !isValidJson(payload) || !edgeFunctionJwt
                    ? undefined
                    : theme.success,
                  borderColor: !selectedEdgeFunction || !isValidJson(payload) || !edgeFunctionJwt
                    ? undefined
                    : theme.success,
                }}
              >
                {isLoading ? 'Running...' : 'Run Function'}
              </Button>
            </Tooltip>
          </Card>

          {/* cURL Preview Card */}
          {selectedEdgeFunction && (
            <Card
              title={
                <Space>
                  <span style={{ color: theme.text }}>cURL Command</span>
                </Space>
              }
              style={cardStyle}
              extra={
                <Button
                  size="small"
                  icon={copiedField === 'curl' ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={() => copyToClipboard(generateCurlCommand(), 'curl')}
                >
                  Copy
                </Button>
              }
            >
              <div style={{
                backgroundColor: currentTheme === 'dark' ? '#1a1a2e' : '#f6f8fa',
                borderRadius: '8px',
                padding: '12px',
                overflow: 'auto',
                maxHeight: '200px',
              }}>
                <pre style={{
                  margin: 0,
                  fontSize: '12px',
                  fontFamily: "'Fira Code', monospace",
                  color: theme.text,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}>
                  {generateCurlCommand()}
                </pre>
              </div>
            </Card>
          )}

          {/* Response Card */}
          {response && (
            <Card
              title={
                <Space>
                  <span style={{ color: theme.text }}>Response</span>
                  <Tag color={response.success ? 'success' : 'error'}>
                    {response.status || (response.success ? 'Success' : 'Error')} {response.statusText}
                  </Tag>
                  {response.responseTime && (
                    <Tag color="blue">{response.responseTime}ms</Tag>
                  )}
                </Space>
              }
              style={cardStyle}
              extra={
                <Button
                  size="small"
                  icon={copiedField === 'response' ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={() => copyToClipboard(
                    responseTab === 'body'
                      ? JSON.stringify(response.data, null, 2)
                      : responseTab === 'headers'
                      ? JSON.stringify(response.headers, null, 2)
                      : JSON.stringify(response.request, null, 2),
                    'response'
                  )}
                >
                  Copy
                </Button>
              }
            >
              <Tabs
                activeKey={responseTab}
                onChange={setResponseTab}
                items={[
                  {
                    key: 'body',
                    label: 'Body',
                    children: (
                      <div style={{
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: `1px solid ${theme.border}`,
                      }}>
                        <ReactAce
                          mode="json"
                          theme={currentTheme === 'dark' ? 'cobalt' : 'github'}
                          value={JSON.stringify(response.data, null, 2)}
                          readOnly
                          height="300px"
                          width="100%"
                          setOptions={{
                            useWorker: false,
                            wrap: true,
                            printMargin: false,
                            showGutter: true,
                            fontSize: 14,
                          }}
                          style={{
                            fontFamily: "'Fira Code', 'Source Code Pro', monospace",
                          }}
                          editorProps={{ $blockScrolling: true }}
                        />
                      </div>
                    ),
                  },
                  {
                    key: 'headers',
                    label: 'Headers',
                    children: (
                      <div style={{
                        backgroundColor: currentTheme === 'dark' ? '#1a1a2e' : '#f6f8fa',
                        borderRadius: '8px',
                        padding: '16px',
                        border: `1px solid ${theme.border}`,
                        maxHeight: '300px',
                        overflow: 'auto',
                      }}>
                        {response.headers && Object.keys(response.headers).length > 0 ? (
                          Object.entries(response.headers).map(([key, value]) => (
                            <div key={key} style={{
                              display: 'flex',
                              marginBottom: '8px',
                              fontFamily: 'monospace',
                              fontSize: '13px',
                            }}>
                              <Text strong style={{ color: theme.primary, minWidth: '200px' }}>
                                {key}:
                              </Text>
                              <Text style={{ color: theme.text, wordBreak: 'break-all' }}>
                                {String(value)}
                              </Text>
                            </div>
                          ))
                        ) : (
                          <Text type="secondary">No headers available</Text>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'request',
                    label: 'Request',
                    children: (
                      <div style={{
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: `1px solid ${theme.border}`,
                      }}>
                        <ReactAce
                          mode="json"
                          theme={currentTheme === 'dark' ? 'cobalt' : 'github'}
                          value={JSON.stringify({
                            url: getEndpointUrl(),
                            method: 'POST',
                            headers: buildHeadersObject(),
                            body: {
                              data: isValidJson(payload) ? JSON.parse(payload) : payload
                            }
                          }, null, 2)}
                          readOnly
                          height="300px"
                          width="100%"
                          setOptions={{
                            useWorker: false,
                            wrap: true,
                            printMargin: false,
                            showGutter: true,
                            fontSize: 14,
                          }}
                          style={{
                            fontFamily: "'Fira Code', 'Source Code Pro', monospace",
                          }}
                          editorProps={{ $blockScrolling: true }}
                        />
                      </div>
                    ),
                  },
                ]}
              />
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default EdgeFunctionSandbox;
